// Crystallisation Service
// Implements Performance Fee & Interest calculations per Loan Agreement

import { collection, addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Crystallisation, Series } from '../types';
import { getSeriesById } from './seriesService';

// Configuration from Agreement
const PERFORMANCE_FEE_RATE = 0.20; // 20%
const HURDLE_RATE = 0.03; // 3% annualized
const CRYSTALLISATION_DATE = '12-31'; // December 31st

export interface CrystallisationCalculation {
  seriesId: string;
  lenderId: string;
  performancePeriodStart: Date;
  performancePeriodEnd: Date;
  
  // NAV Values
  startNAV: number;
  endNAV: number;
  
  // Calculations
  grossProfit: number;
  grossProfitPercent: number;
  daysInPeriod: number;
  annualisedReturnRate: number;
  
  // Conditions
  hurdleRateMet: boolean;
  highWaterMarkExceeded: boolean;
  
  // Results
  performanceFee: number;
  interest: number;
  
  // Scenario determination
  scenario: 'loss' | 'below_hurdle' | 'underwater_recovery' | 'full_performance_fee';
  
  // High Water Mark
  previousHighWaterMark: number;
  newHighWaterMark: number;
}

/**
 * Calculate crystallisation for a series
 */
export const calculateCrystallisation = async (
  series: Series,
  crystallisationDate: Date
): Promise<CrystallisationCalculation> => {
  // Determine performance period - convert Timestamp to Date if needed
  const periodStart = series.issueDate instanceof Date ? series.issueDate : new Date(series.issueDate);
  const periodEnd = crystallisationDate;
  
  // Calculate days in period
  const daysInPeriod = Math.floor(
    (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)
  );
  
  // Get NAV values
  // Check if there have been previous crystallisations
  const previousCrystallisations = await getSeriesCrystallisations(series.id);
  
  let startNAV: number;
  if (previousCrystallisations.length > 0) {
    // Use the endNAV from the last crystallisation (before any capitalization)
    // This prevents double-counting gains from previous periods
    const lastCryst = previousCrystallisations.sort((a, b) => 
      b.date.getTime() - a.date.getTime()
    )[0];
    startNAV = lastCryst.endNAV;
  } else {
    // First crystallisation: start from principal amount
    startNAV = series.principalAmount;
  }
  
  const endNAV = series.currentNAV; // Current market value
  
  // Calculate Gross Profit
  const grossProfit = endNAV - startNAV;
  const grossProfitPercent = startNAV > 0 ? (grossProfit / startNAV) * 100 : 0;
  
  // Calculate Annualized Return Rate
  const annualisedReturnRate = daysInPeriod > 0
    ? (grossProfit / startNAV) * (365 / daysInPeriod) * 100
    : 0;
  
  // Get High Water Mark
  const previousHighWaterMark = series.highWaterMark;
  
  // Check conditions
  const hurdleRateMet = annualisedReturnRate > HURDLE_RATE * 100;
  const highWaterMarkExceeded = endNAV > previousHighWaterMark;
  const isProfitable = grossProfit > 0;
  
  // Determine scenario and calculate fees
  let scenario: CrystallisationCalculation['scenario'];
  let performanceFee = 0;
  let interest = 0;
  let newHighWaterMark = previousHighWaterMark;
  
  if (!isProfitable) {
    // Scenario 1: Loss
    scenario = 'loss';
    performanceFee = 0;
    interest = grossProfit; // Negative (lender bears loss)
    newHighWaterMark = previousHighWaterMark; // HWM unchanged
  } else if (!hurdleRateMet) {
    // Scenario 2: Positive return but below hurdle
    scenario = 'below_hurdle';
    performanceFee = 0;
    interest = grossProfit;
    newHighWaterMark = endNAV; // HWM updates to current NAV
  } else if (!highWaterMarkExceeded) {
    // Scenario 3: Above hurdle but underwater (recovering from loss)
    scenario = 'underwater_recovery';
    performanceFee = 0;
    interest = grossProfit;
    newHighWaterMark = previousHighWaterMark; // HWM unchanged
  } else {
    // Scenario 4: All conditions met - charge performance fee
    scenario = 'full_performance_fee';
    performanceFee = grossProfit * PERFORMANCE_FEE_RATE;
    interest = grossProfit - performanceFee;
    newHighWaterMark = endNAV; // HWM resets to new high
  }
  
  return {
    seriesId: series.id,
    lenderId: series.lenderId,
    performancePeriodStart: periodStart,
    performancePeriodEnd: periodEnd,
    startNAV,
    endNAV,
    grossProfit,
    grossProfitPercent,
    daysInPeriod,
    annualisedReturnRate,
    hurdleRateMet,
    highWaterMarkExceeded,
    performanceFee,
    interest,
    scenario,
    previousHighWaterMark,
    newHighWaterMark,
  };
};

/**
 * Execute crystallisation - save to database and update series
 */
export const executeCrystallisation = async (
  calculation: CrystallisationCalculation,
  paymentMethod: 'capitalised' | 'cash_payment',
  executedBy: string = 'admin'
): Promise<string> => {
  try {
    // Create crystallisation record
    const crystallisationData: Omit<Crystallisation, 'id'> = {
      date: calculation.performancePeriodEnd,
      seriesId: calculation.seriesId,
      lenderId: calculation.lenderId,
      performancePeriodStart: calculation.performancePeriodStart,
      performancePeriodEnd: calculation.performancePeriodEnd,
      
      startNAV: calculation.startNAV,
      endNAV: calculation.endNAV,
      
      grossProfit: calculation.grossProfit,
      annualisedReturnRate: calculation.annualisedReturnRate,
      hurdleRateMet: calculation.hurdleRateMet,
      highWaterMarkExceeded: calculation.highWaterMarkExceeded,
      
      performanceFee: calculation.performanceFee,
      interest: calculation.interest,
      
      previousHighWaterMark: calculation.previousHighWaterMark,
      newHighWaterMark: calculation.newHighWaterMark,
      
      paymentMethod,
      paymentStatus: paymentMethod === 'capitalised' ? 'capitalised' : 'pending',
      
      createdAt: new Date(),
      createdBy: executedBy,
    };
    
    // Save to Firestore
    const docRef = await addDoc(collection(db, 'crystallisations'), {
      ...crystallisationData,
      date: Timestamp.fromDate(crystallisationData.date),
      performancePeriodStart: Timestamp.fromDate(crystallisationData.performancePeriodStart),
      performancePeriodEnd: Timestamp.fromDate(crystallisationData.performancePeriodEnd),
      createdAt: Timestamp.fromDate(crystallisationData.createdAt),
    });
    
    // FIX Issue #005: Deduct performance fee from series NAV
    // Get current series data
    const series = await getSeriesById(calculation.seriesId);
    if (!series) {
      throw new Error('Series not found');
    }
    
    // Calculate new NAV after deducting performance fee
    const newSeriesNAV = series.currentNAV - calculation.performanceFee;
    
    // Update series with new NAV and High Water Mark
    const { updateSeries } = await import('./seriesService');
    await updateSeries(calculation.seriesId, {
      currentNAV: newSeriesNAV, // Deduct performance fee from NAV
      highWaterMark: calculation.newHighWaterMark,
      updatedAt: new Date(),
    });
    
    // FIX Issue #006: Add performance fee to fund fees account
    if (calculation.performanceFee > 0) {
      const { updateDoc, doc, getDoc, setDoc, increment } = await import('firebase/firestore');
      const fundFeesRef = doc(db, 'poolNAV', 'current');
      const fundDoc = await getDoc(fundFeesRef);
      
      if (!fundDoc.exists()) {
        await setDoc(fundFeesRef, {
          fundFees: calculation.performanceFee,
          updatedAt: Timestamp.now(),
        });
      } else {
        await updateDoc(fundFeesRef, {
          fundFees: increment(calculation.performanceFee),
          updatedAt: Timestamp.now(),
        });
      }
    }
    
    // If capitalized, track interest separately
    if (paymentMethod === 'capitalised' && calculation.interest !== 0) {
      const newCapitalizedInterest = (series.capitalizedInterest || 0) + calculation.interest;
      
      await updateSeries(calculation.seriesId, {
        capitalizedInterest: newCapitalizedInterest,
        updatedAt: new Date(),
      });
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error executing crystallisation:', error);
    throw new Error('Failed to execute crystallisation');
  }
};

/**
 * Get crystallisation history for a series
 */
export const getSeriesCrystallisations = async (seriesId: string): Promise<Crystallisation[]> => {
  try {
    const q = query(
      collection(db, 'crystallisations'),
      where('seriesId', '==', seriesId)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate(),
      performancePeriodStart: doc.data().performancePeriodStart?.toDate(),
      performancePeriodEnd: doc.data().performancePeriodEnd?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
    })) as Crystallisation[];
  } catch (error) {
    console.error('Error getting crystallisations:', error);
    throw new Error('Failed to fetch crystallisation history');
  }
};

/**
 * Get all crystallisations for a lender
 */
export const getLenderCrystallisations = async (lenderId: string): Promise<Crystallisation[]> => {
  try {
    const q = query(
      collection(db, 'crystallisations'),
      where('lenderId', '==', lenderId)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate(),
      performancePeriodStart: doc.data().performancePeriodStart?.toDate(),
      performancePeriodEnd: doc.data().performancePeriodEnd?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
    })) as Crystallisation[];
  } catch (error) {
    console.error('Error getting lender crystallisations:', error);
    throw new Error('Failed to fetch lender crystallisation history');
  }
};

/**
 * Get scenario explanation
 */
export const getScenarioExplanation = (scenario: CrystallisationCalculation['scenario']): string => {
  switch (scenario) {
    case 'loss':
      return 'Loss: Gross Profit ≤ 0. Lender bears full loss. No performance fee charged.';
    case 'below_hurdle':
      return 'Below Hurdle: Positive return but annualized return ≤ 3%. No performance fee charged.';
    case 'underwater_recovery':
      return 'Underwater Recovery: Above hurdle but NAV below High Water Mark. No performance fee charged.';
    case 'full_performance_fee':
      return 'Performance Fee Applicable: Gross Profit > 0, Return > 3%, NAV > High Water Mark. 20% performance fee charged.';
    default:
      return '';
  }
};

/**
 * Format crystallisation summary
 */
export const formatCrystallisationSummary = (calc: CrystallisationCalculation): string => {
  return `
Performance Period: ${calc.performancePeriodStart.toLocaleDateString()} - ${calc.performancePeriodEnd.toLocaleDateString()}
Days: ${calc.daysInPeriod}

Starting NAV: $${calc.startNAV.toLocaleString()}
Ending NAV: $${calc.endNAV.toLocaleString()}
Gross Profit: $${calc.grossProfit.toLocaleString()} (${calc.grossProfitPercent.toFixed(2)}%)

Annualized Return: ${calc.annualisedReturnRate.toFixed(2)}%
Hurdle Rate (3%): ${calc.hurdleRateMet ? '✓ Met' : '✗ Not Met'}
High Water Mark ($${calc.previousHighWaterMark.toLocaleString()}): ${calc.highWaterMarkExceeded ? '✓ Exceeded' : '✗ Not Exceeded'}

Scenario: ${calc.scenario}
Performance Fee: $${calc.performanceFee.toLocaleString()}
Interest to Lender: $${calc.interest.toLocaleString()}

New High Water Mark: $${calc.newHighWaterMark.toLocaleString()}
  `.trim();
};
